import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { userApi, type ConsentPreferences, type ConsentPatchBody } from '../../services/userApi';
import { GeoDefaultsDisclosure } from '../../components/consent/GeoDefaultsDisclosure';

type ToggleKey = keyof ConsentPatchBody;

interface ToggleSpec {
  key: ToggleKey | 'strictlyNecessary';
  label: string;
  description: string;
  readOnly?: boolean;
}

/**
 * Per spec §10.7, the consent state has six flags. `strictlyNecessary`
 * is always-on (the site can't function without it) and is not in the
 * PATCH body. Each toggle is paired with a plain-language description
 * so users understand what they are agreeing to — this is both a UX
 * and a legal requirement (informed consent).
 */
const TOGGLES: ToggleSpec[] = [
  {
    key: 'strictlyNecessary',
    label: 'Strictly necessary',
    description:
      'Required for the site to work — session cookies, security, and load balancing. This cannot be turned off.',
    readOnly: true,
  },
  {
    key: 'analytics',
    label: 'Analytics',
    description:
      'Lets us measure aggregate usage (page views, search performance) so we can fix slow pages and broken filters. Never tied to your identity in advertiser reports.',
  },
  {
    key: 'personalization',
    label: 'Personalization',
    description:
      'Uses your saved preferences (preferred retailers, brands, calibers) to rank search results and recommendations.',
  },
  {
    key: 'behavioralAdvertising',
    label: 'Behavioral advertising',
    description:
      'Allows sponsored placements that are targeted based on your browsing behavior on Round Recon. When off, you’ll still see general sponsored listings but they won’t be personalized.',
  },
  {
    key: 'advertiserDataSharing',
    label: 'Advertiser data sharing',
    description:
      'Allows advertisers to see aggregated, anonymized reports that include your interactions. Independent of behavioral advertising — turning this off does not affect ad serving.',
  },
  {
    key: 'marketingEmail',
    label: 'Marketing email',
    description:
      'Lets us send occasional product news and feature announcements. Separate from your alert emails — alerts always go through regardless of this setting.',
  },
];

type LocalState = Record<ToggleKey | 'strictlyNecessary', boolean>;

function toLocal(consent: ConsentPreferences): LocalState {
  return {
    strictlyNecessary: consent.strictlyNecessary,
    analytics: consent.analytics,
    personalization: consent.personalization,
    behavioralAdvertising: consent.behavioralAdvertising,
    advertiserDataSharing: consent.advertiserDataSharing,
    marketingEmail: consent.marketingEmail,
  };
}

function diff(initial: LocalState, current: LocalState): ConsentPatchBody {
  const patch: ConsentPatchBody = {};
  for (const key of [
    'analytics',
    'personalization',
    'behavioralAdvertising',
    'advertiserDataSharing',
    'marketingEmail',
  ] as ToggleKey[]) {
    if (initial[key] !== current[key]) patch[key] = current[key];
  }
  return patch;
}

export function PrivacyPage() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['user', 'privacy'],
    queryFn: () => userApi.getPrivacy(),
  });

  const [initial, setInitial] = useState<LocalState | null>(null);
  const [local, setLocal] = useState<LocalState | null>(null);
  const [saved, setSaved] = useState(false);

  // Seed the editable consent snapshot from the loaded record, re-syncing if
  // the query data changes. "Adjust state during render" instead of an effect.
  const [syncedConsent, setSyncedConsent] = useState(query.data);
  if (query.data && query.data !== syncedConsent) {
    setSyncedConsent(query.data);
    const snapshot = toLocal(query.data.consent);
    setInitial(snapshot);
    setLocal(snapshot);
    setSaved(false);
  }

  const patchMutation = useMutation({
    mutationFn: (patch: ConsentPatchBody) => userApi.patchPrivacy(patch),
    onSuccess: (data) => {
      const snapshot = toLocal(data.consent);
      setInitial(snapshot);
      setLocal(snapshot);
      setSaved(true);
      return queryClient.invalidateQueries({ queryKey: ['user', 'privacy'] });
    },
  });

  function onToggle(key: ToggleKey) {
    setLocal((s) => (s ? { ...s, [key]: !s[key] } : s));
    setSaved(false);
  }

  function onSave() {
    if (!initial || !local) return;
    const patch = diff(initial, local);
    if (Object.keys(patch).length === 0) return;
    patchMutation.mutate(patch);
  }

  if (query.isLoading || !local) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress aria-label="Loading privacy preferences" />
      </Box>
    );
  }

  if (query.isError) {
    return (
      <Alert severity="error" role="alert">
        Failed to load your privacy preferences: {(query.error as Error).message}
      </Alert>
    );
  }

  const dirty = initial && Object.keys(diff(initial, local)).length > 0;

  return (
    <Box>
      <Typography variant="h1" component="h1" sx={{ fontSize: '1.6rem', mb: 1 }}>
        Privacy preferences
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Each setting controls a specific use of your data. You can change these at any time.
      </Typography>

      <GeoDefaultsDisclosure
        region={query.data?.consent.region}
        isAtDefaults={!query.data?.consent.consentedAt}
      />

      {saved ? (
        <Alert severity="success" role="status" sx={{ mb: 2 }}>
          Your privacy preferences have been updated.
        </Alert>
      ) : null}
      {patchMutation.isError ? (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          Couldn’t save your preferences: {(patchMutation.error as Error).message}
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        <Stack spacing={3} role="group" aria-label="Consent preferences">
          {TOGGLES.map((toggle) => {
            const checked = local[toggle.key];
            const id = `consent-${toggle.key}`;
            return (
              <Box key={toggle.key}>
                <FormControlLabel
                  control={
                    <Switch
                      id={id}
                      checked={checked}
                      onChange={() => !toggle.readOnly && onToggle(toggle.key as ToggleKey)}
                      disabled={toggle.readOnly}
                      inputProps={{
                        'aria-describedby': `${id}-help`,
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography component="span" variant="subtitle1">
                        {toggle.label}
                      </Typography>
                      {toggle.readOnly ? (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ ml: 1 }}
                          color="text.secondary"
                        >
                          (Always on)
                        </Typography>
                      ) : null}
                    </Box>
                  }
                />
                <Typography id={`${id}-help`} variant="body2" color="text.secondary" sx={{ ml: 6 }}>
                  {toggle.description}
                </Typography>
              </Box>
            );
          })}
        </Stack>
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={!dirty || patchMutation.isPending}
            aria-busy={patchMutation.isPending}
          >
            {patchMutation.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}

export default PrivacyPage;
